import type { Edge } from '@xyflow/react';
import type { OntologyNode } from '@/types/ontologyGraph';
import type { OntologyFlowNode } from './types';
import { isMoreNodeId } from '@/types/ontologyGraph';
import { GENE_NODE_ID_PREFIX } from '@/services/ontologyGraph.service';

type BuildDiseaseOntologyTreeParams = {
  nodes: Map<string, OntologyNode>;
  edges: { source: string; target: string }[];
  onToggle: (nodeId: string) => void;
  onCollapse: (nodeId: string) => void;
  onNavigate: (nodeId: string) => void;
  openMoreList: (moreNodeId: string) => void;
};

export function buildDiseaseOntologyTree({
  nodes,
  edges,
  onToggle,
  onCollapse,
  onNavigate,
  openMoreList,
}: BuildDiseaseOntologyTreeParams): { flowNodes: OntologyFlowNode[]; flowEdges: Edge[] } {
  const flowNodes: OntologyFlowNode[] = [];
  const flowEdges: Edge[] = [];

  const diseaseEdgeSet = new Set<string>();

  const parentById = new Map<string, string>();
  for (const e of edges) {
    parentById.set(e.target, e.source);
  }

  for (const [id, node] of nodes) {
    const isGeneId = id.startsWith(GENE_NODE_ID_PREFIX);
    const isGeneOnlySynthetic =
      id.startsWith('__show_more_genes_') ||
      id.startsWith('__show_related_diseases_') ||
      id.startsWith('__other_genes_');

    if (isGeneId || isGeneOnlySynthetic) continue;

    const parentId = parentById.get(id);

    if (isMoreNodeId(id)) {
      flowNodes.push({
        id,
        type: 'action',
        position: { x: 0, y: 0 },
        data: {
          label: node.label,
          actionType: 'more_children',
          meta: {},
          onClick: () => openMoreList(id),
        },
      } as OntologyFlowNode);

      if (parentId) {
        const edgeKey = `${parentId}→${id}`;
        if (!diseaseEdgeSet.has(edgeKey)) {
          diseaseEdgeSet.add(edgeKey);
          flowEdges.push({
            id: `e-${parentId}-${id}`,
            source: parentId,
            target: id,
            type: 'smoothstep',
            style: { stroke: '#c8d6e5', strokeWidth: 1.5 },
          });
        }
      }
      continue;
    }

    const isDiseaseNode = node.type === 'disease' || node.type === undefined;
    if (!isDiseaseNode) continue;

    const parentNode = parentId ? nodes.get(parentId) : undefined;
    const parentIsDisease =
      !parentId ||
      (parentNode &&
        !parentId.startsWith(GENE_NODE_ID_PREFIX) &&
        !parentId.startsWith('__') &&
        (parentNode.type === 'disease' || parentNode.type === undefined));

    flowNodes.push({
      id,
      type: 'disease',
      position: { x: 0, y: 0 },
      data: {
        label: node.label,
        cui: node.cui,
        hasChildren: node.hasChildren,
        isExpanded: node.expanded,
        numDescendants: node.hiddenChildrenCount,
        onToggle,
        onCollapse,
        onNavigate,
      },
    } as OntologyFlowNode);

    if (parentId && parentIsDisease) {
      const edgeKey = `${parentId}→${id}`;
      if (!diseaseEdgeSet.has(edgeKey)) {
        diseaseEdgeSet.add(edgeKey);
        flowEdges.push({
          id: `e-${parentId}-${id}`,
          source: parentId,
          target: id,
          type: 'smoothstep',
          style: { stroke: '#c8d6e5', strokeWidth: 1.5 },
        });
      }
    }
  }

  return { flowNodes, flowEdges };
}
